import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, message, Spin, ConfigProvider } from "antd";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { api, endpoints } from "../../utils/axiosInstance";
import { useMealMate } from "../../context/MealMateContext";

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #2c3e50;
`;

const SubscriptionGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;

  @media (min-width: 1024px) {
    flex-wrap: nowrap;
  }
`;

const PlanCard = styled(Card)`
  width: 300px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: ${(props) =>
    props.$isPopular ? "2px solid #37b24d" : "1px solid #eee"};
  position: relative;
  overflow: visible;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }

  .ant-card-body {
    padding: 24px;
    text-align: center;
  }
`;

const PlanTitle = styled.h2`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const BillingType = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const PriceDisplay = styled.div`
  margin: 1.5rem 0;
  text-align: center;

  .original-price {
    text-decoration: line-through;
    color: #999;
    font-size: 1.5rem;
    display: block;
  }

  .price {
    color: #37b24d;
    font-size: 2rem;
    font-weight: bold;
    margin: 0.5rem 0;
  }

  .per-month {
    color: #666;
    font-size: 0.9rem;
  }
`;

const HeaderInfo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  .currency {
    color: #666;
    margin-right: 1rem;
  }
`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user } = useMealMate();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.subscriptions.list);
      setSubscriptions(response.data);
    } catch (error) {
      messageApi.error("Không thể tải thông tin gói subscription");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const handleSubscribe = async (subscriptionId) => {
    if (!user) {
      messageApi.error("Vui lòng đăng nhập để đăng ký gói");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(endpoints.payment.createPaymentLink, {
        subscriptionId,
      });

      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error("Không nhận được link thanh toán");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        messageApi.error(error.response.data.message);
      } else {
        messageApi.error("Có lỗi xảy ra khi tạo link thanh toán");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price);
  };

  return (
    <ConfigProvider>
      {contextHolder}
      <PageWrapper>
        <PageTitle>Chọn Gói Đăng Ký</PageTitle>
        <HeaderInfo>
          <span className="currency">Đơn vị tiền tệ: VND</span>
        </HeaderInfo>

        <Spin spinning={loading}>
          <SubscriptionGrid>
            {subscriptions.map((plan) => (
              <PlanCard key={plan._id} $isPopular={plan.name === "12 MONTHS"}>
                <PlanTitle>
                  {plan.name === "1 MONTH"
                    ? "1 THÁNG"
                    : plan.name === "3 MONTHS"
                    ? "3 THÁNG"
                    : plan.name === "12 MONTHS"
                    ? "12 THÁNG"
                    : plan.name}
                </PlanTitle>
                <BillingType>
                  {plan.duration === 1
                    ? "Thanh toán hàng tháng"
                    : plan.duration === 3
                    ? "Thanh toán hàng quý"
                    : "Thanh toán hàng năm"}
                </BillingType>

                <PriceDisplay>
                  <div className="original-price">
                    {formatPrice(plan.originalPrice)}
                  </div>
                  <div className="price">
                    {formatPrice(plan.discountedPrice)}
                  </div>
                  <div className="per-month">
                    {formatPrice(plan.pricePerMonth)}/tháng
                  </div>
                </PriceDisplay>

                <Button
                  type="primary"
                  size="large"
                  block
                  style={{
                    backgroundColor: "#37b24d",
                    height: "auto",
                    padding: "12px",
                    fontSize: "1rem",
                    borderRadius: "8px",
                  }}
                  onClick={() => handleSubscribe(plan._id)}
                >
                  CHỌN GÓI{" "}
                  {plan.duration === 1
                    ? "1 THÁNG"
                    : plan.duration === 3
                    ? "3 THÁNG"
                    : "12 THÁNG"}
                </Button>
              </PlanCard>
            ))}
          </SubscriptionGrid>
        </Spin>
      </PageWrapper>
    </ConfigProvider>
  );
};

export default PaymentPage;
